import { useEffect, useState } from 'react';
import { Users, Loader2, Globe } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { MetaApp } from '../types/meta.types';

interface ProjectOption {
    id: number;
    name: string;
}

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** App sendo editado — projetos atuais carregados dele. */
    app: MetaApp | null;
    /** Projetos disponíveis (todos os que o user tem acesso). */
    availableProjects: ProjectOption[];
    /** Callback ao salvar. Recebe a lista nova de project_ids. Async pra mostrar loading. */
    onSubmit: (appId: number, projectIds: number[]) => Promise<void> | void;
    saving?: boolean;
}

/**
 * Modal compacto APENAS pra ajustar a lista de projetos vinculados ao app.
 * Diferente do CreateEditAppDialog (form gigante), aqui o user só clica em
 * Users na lista de apps → seleciona/desmarca projects → Salva.
 *
 * Empty selection = app vira público.
 */
export default function EditAppProjectsDialog({
    open, onOpenChange, app, availableProjects, onSubmit, saving = false,
}: Props) {
    const [selected, setSelected] = useState<number[]>([]);

    // Carrega seleção atual ao abrir
    useEffect(() => {
        if (!open || !app) return;
        const initial = (app.project_ids && app.project_ids.length > 0)
            ? app.project_ids
            : (app.project_id ? [app.project_id] : []);
        setSelected(initial);
    }, [open, app]);

    if (!app) return null;

    const toggle = (id: number) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );
    };

    const handleSave = async () => {
        await onSubmit(app.id, selected);
    };

    const hasChange = (() => {
        const initial = new Set(
            app.project_ids && app.project_ids.length > 0
                ? app.project_ids
                : app.project_id ? [app.project_id] : [],
        );
        if (initial.size !== selected.length) return true;
        return selected.some((id) => !initial.has(id));
    })();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-violet-600" />
                        Projetos do app
                    </DialogTitle>
                    <DialogDescription>
                        Ajustar quais projetos podem ver e usar <strong>{app.app_name}</strong>.
                        Sem nenhum selecionado = app público (visível pra todos).
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">
                            {selected.length === 0 ? (
                                <span className="flex items-center gap-2 text-emerald-700">
                                    <Globe className="w-4 h-4" />
                                    Público (todos os projetos)
                                </span>
                            ) : (
                                <span>
                                    {selected.length} projeto{selected.length > 1 ? 's' : ''} selecionado{selected.length > 1 ? 's' : ''}
                                </span>
                            )}
                        </Label>
                        {selected.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setSelected([])}
                                className="text-xs text-gray-500 hover:underline"
                            >
                                limpar todos
                            </button>
                        )}
                    </div>

                    {availableProjects.length === 0 ? (
                        <p className="text-sm text-gray-400 italic p-4 text-center border rounded-lg">
                            Nenhum projeto disponível.
                        </p>
                    ) : (
                        <div className="border rounded-lg max-h-72 overflow-y-auto bg-white">
                            {availableProjects.map((p) => {
                                const checked = selected.includes(p.id);
                                return (
                                    <label
                                        key={p.id}
                                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b last:border-b-0 transition-colors ${
                                            checked ? 'bg-violet-50/40 hover:bg-violet-50/60' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(p.id)}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm flex-1">{p.name}</span>
                                        {checked && (
                                            <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700 bg-violet-50">
                                                vinculado
                                            </Badge>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!hasChange || saving}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Salvar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
